import React, { useState, useEffect } from 'react';
import { Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import SearchableDropdown from '../common/SearchableDropdown';
import NumericInput from '../common/NumericInput';
import DatePicker from '../common/DatePicker';
import { adminService } from '../../services/api';
import { salesInvoiceService } from '../../services/modules/inventory';
import { invoiceService, appointmentInvoiceService } from '../../services/modules/health';
import { diagnosticService } from '../../services/modules/health/diagnosticService';
import { useToast } from '../../context/ToastContext';
import { toSafeFixed } from '../../utils/numberUtils';

interface OrderCommissionFormProps {
  onSave: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  resetForm?: boolean;
}

interface CommissionItem {
  item_type: string;
  item_id: number;
  item_name: string;
  item_rate: number;
  item_qty: number;
  commission_type: string;
  commission_percentage: number;
  commission_value: number;
  gst_percentage: number;
  gst_amount: number;
  cess_percentage: number;
  cess_amount: number;
  total_amount: number;
  invoice_total_amount: number;
  discount_percentage: number;
  discount_amount: number;
  roundoff: number;
  final_amount: number;
}

const OrderCommissionForm: React.FC<OrderCommissionFormProps> = ({ onSave, isCollapsed, onToggleCollapse, resetForm }) => {
  const [agencyOptions, setAgencyOptions] = useState<{value: string, label: string}[]>([]);
  const [agencies, setAgencies] = useState<any[]>([]);
  const { showToast } = useToast();

  const generateOrderNumber = () => {
    const now = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 1;
    
    // Format: ddmmyyyyhhmmsssfff
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    
    const timestamp = `${day}${month}${year}${hours}${minutes}${seconds}${milliseconds}`;
    return `OC-${tenantId}${timestamp}`;
  };

  const generatePaymentNumber = () => {
    const now = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 1;
    
    // Format: ddmmyyyyhhmmssfff
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    
    const timestamp = `${day}${month}${year}${hours}${minutes}${seconds}${milliseconds}`;
    return `CPAY-${tenantId}${timestamp}`;
  };

  const [formData, setFormData] = useState({
    order_commission_number: generateOrderNumber(),
    invoice_type: 'Tests',
    commission_type: 'Invoice',
    invoice_id: '',
    invoice_number: '',
    agency_id: '',
    agency_name: '',
    agency_phone: '',
    invoice_final_bill_amount: 0,
    commission_value: 10,
    notes: '',
    disc_percentage: 0,
    roundoff: 0,
    payment_amount: 0,
    payment_mode: 'CASH',
    payment_number: generatePaymentNumber(),
    instrument_number: '',
    instrument_date: new Date().toISOString().split('T')[0],
    bank_name: '',
    branch_name: '',
    ifsc_code: '',
    transaction_reference: '',
    payment_remarks: ''
  });

  const [invoiceOptions, setInvoiceOptions] = useState<{value: string, label: string}[]>([]);

  const [allTests, setAllTests] = useState<any[]>([]);

  const [paymentModes, setPaymentModes] = useState<string[]>(['CASH']);

  const [items, setItems] = useState<CommissionItem[]>([{
    item_type: 'Test',
    item_id: 0,
    item_name: '',
    item_rate: 0,
    item_qty: 1,
    commission_type: 'Percentage',
    commission_percentage: 0,
    commission_value: 0,
    gst_percentage: 0,
    gst_amount: 0,
    cess_percentage: 0,
    cess_amount: 0,
    total_amount: 0,
    invoice_total_amount: 0,
    discount_percentage: 0,
    discount_amount: 0,
    roundoff: 0,
    final_amount: 0
  }]);

  useEffect(() => {
    loadAllAgencies();
    loadPaymentModes();
    // Invoices will be loaded when agent is selected
  }, []);

  useEffect(() => {
    if (formData.invoice_type === 'Tests') {
      loadAllTests();
    } else {
      setAllTests([]);
    }
  }, [formData.invoice_type]);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        order_commission_number: generateOrderNumber(),
        invoice_type: 'Tests',
        commission_type: 'Invoice',
        invoice_id: '',
        invoice_number: '',
        agency_id: '',
        agency_name: '',
        agency_phone: '',
        invoice_final_bill_amount: 0,
        commission_value: 10,
        notes: '',
        disc_percentage: 0,
        roundoff: 0,
        payment_amount: 0,
        payment_mode: 'CASH',
        payment_number: generatePaymentNumber(),
        instrument_number: '',
        instrument_date: new Date().toISOString().split('T')[0],
        bank_name: '',
        branch_name: '',
        ifsc_code: '',
        transaction_reference: '',
        payment_remarks: ''
      });
      setItems([{
        item_type: 'Test',
        item_id: 0,
        item_name: '',
        item_rate: 0,
        item_qty: 1,
        commission_type: 'Percentage',
        commission_percentage: 0,
        commission_value: 0,
        gst_percentage: 0,
        gst_amount: 0,
        cess_percentage: 0,
        cess_amount: 0,
        total_amount: 0,
        invoice_total_amount: 0,
        discount_percentage: 0,
        discount_amount: 0,
        roundoff: 0,
        final_amount: 0
      }]);
    }
  }, [resetForm]);

  const loadAllAgencies = async () => {
    try {
      const response = await adminService.getAgencies({ per_page: 100 });
      console.log('Full agencies response:', response);
      const agenciesData = response?.data || [];
      console.log('Extracted agencies data:', agenciesData);
      console.log('Number of agencies:', agenciesData.length);
      
      if (Array.isArray(agenciesData)) {
        setAgencies(agenciesData);
        const options = agenciesData.map((agency: any) => ({
          value: agency.id.toString(),
          label: `${agency.name} - ${agency.phone}`
        }));
        setAgencyOptions(options);
      } else {
        console.error('Agencies data is not an array:', agenciesData);
        setAgencies([]);
        setAgencyOptions([]);
      }
    } catch (error) {
      console.error('Error loading agencies:', error);
      showToast('error', 'Failed to load agencies');
      setAgencies([]);
      setAgencyOptions([]);
    }
  };

  const loadPaymentModes = async () => {
    try {
      const response = await adminService.getTenantSettings();
      if (response.data?.payment_modes) {
        setPaymentModes(response.data.payment_modes);
      }
    } catch (error) {
      console.log('Failed to load payment modes, using defaults');
    }
  };

  const loadAllTests = async () => {
    try {
      const response = await diagnosticService.getTests({ per_page: 1000 }) as any;
      const testsData = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setAllTests(testsData);
    } catch (error) {
      console.error('Error loading tests:', error);
      setAllTests([]);
    }
  };

  const loadAgencies = async (search: string) => {
    try {
      const response = await adminService.getAgencies({ search, per_page: 50 });
      return response.data.map((agency: any) => ({
        value: agency.id.toString(),
        label: `${agency.phone} | ${agency.name}`
      }));
    } catch (error) {
      return [];
    }
  };

  const loadInvoices = async (invoiceType: string = formData.invoice_type, agentId?: string) => {
    try {
      setInvoiceOptions([]); // Clear previous invoices
      let response;
      
      if (invoiceType === 'Products') {
        response = await salesInvoiceService.getSalesInvoices({ page_size: 100 });
      } else if (invoiceType === 'Tests') {
        const params: any = { per_page: 100, has_commission: false };
        if (agentId) {
          params.agent_id = agentId;
          console.log('Loading test invoices with agent_id:', agentId, 'and has_commission: false');
        } else {
          console.log('Loading test invoices without agent_id filter but with has_commission: false');
        }
        response = await invoiceService.getTestInvoices(params);
      } else if (invoiceType === 'Clinic') {
        response = await appointmentInvoiceService.getAppointmentInvoices({ per_page: 100 });
      }
      
      console.log('Full response:', response);
      const invoiceData = response?.data || [];
      console.log('Extracted invoice data:', invoiceData);
      console.log('Number of invoices:', invoiceData.length);
      
      if (!Array.isArray(invoiceData)) {
        console.error('Invoice data is not an array:', invoiceData);
        setInvoiceOptions([]);
        return;
      }
      
      //const invoicesWithAgency = invoiceData.filter((invoice: any) => invoice.agency_id != null && invoice.agency_id !== '');

      const invoicesWithAgency = invoiceData;
      
      let options;
      if (invoiceType === 'Sales') {
        options = invoicesWithAgency.map((invoice: any) => ({
          value: invoice.id.toString(),
          label: `${invoice.invoice_number || 'N/A'}`
        }));
      } else {
        options = invoicesWithAgency.map((invoice: any) => ({
          value: invoice.id.toString(),
          label: `${invoice.invoice_number || 'N/A'}`
        }));
      }
      
      console.log('Setting invoice options:', options.length, 'options');
      setInvoiceOptions(options);
    } catch (error) {
      console.error('Error loading invoices:', error);
      setInvoiceOptions([]);
    }
  };

  const handleAgencyChange = (value: string | number | (string | number)[]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setFormData(prev => ({ 
        ...prev, 
        agency_id: '',
        agency_name: '',
        agency_phone: '',
        invoice_id: '',
        invoice_number: '',
        invoice_final_bill_amount: 0
      }));
      setInvoiceOptions([]); // Clear invoices when no agent selected
      return;
    }
    const agencyId = Array.isArray(value) ? value[0] : value;
    const selectedAgency = agencies.find(a => a.id.toString() === agencyId.toString());
    
    if (selectedAgency) {
      console.log('Agency selected:', selectedAgency);
      // Reset entire form when changing agent
      setFormData({
        order_commission_number: generateOrderNumber(),
        invoice_type: 'Tests',
        commission_type: 'Invoice',
        invoice_id: '',
        invoice_number: '',
        agency_id: selectedAgency.id.toString(),
        agency_name: selectedAgency.name,
        agency_phone: selectedAgency.phone,
        invoice_final_bill_amount: 0,
        commission_value: 10,
        notes: '',
        disc_percentage: 0,
        roundoff: 0,
        payment_amount: 0,
        payment_mode: 'CASH',
        payment_number: generatePaymentNumber(),
        instrument_number: '',
        instrument_date: new Date().toISOString().split('T')[0],
        bank_name: '',
        branch_name: '',
        ifsc_code: '',
        transaction_reference: '',
        payment_remarks: ''
      });
      // Clear invoice options immediately when changing agent
      setInvoiceOptions([]);
      // Reset items to initial state
      setItems([{
        item_type: 'Test',
        item_id: 0,
        item_name: '',
        item_rate: 0,
        item_qty: 1,
        commission_type: 'Percentage',
        commission_percentage: 10,
        commission_value: 0,
        gst_percentage: 0,
        gst_amount: 0,
        cess_percentage: 0,
        cess_amount: 0,
        total_amount: 0,
        invoice_total_amount: 0,
        discount_percentage: 0,
        discount_amount: 0,
        roundoff: 0,
        final_amount: 0
      }]);
      // Load invoices for the selected agent
      console.log('Loading invoices for agent:', selectedAgency.id.toString(), 'with invoice type:', 'Tests');
      loadInvoices('Tests', selectedAgency.id.toString());
    }
  };

  const handleInvoiceTypeChange = async (value: string | number | (string | number)[]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setInvoiceOptions([]);
      setFormData(prev => ({ 
        ...prev, 
        invoice_id: '',
        invoice_number: ''
      }));
      return;
    }
    const invoiceType = Array.isArray(value) ? value[0] : value;
    console.log('Invoice type changed to:', invoiceType);
    setFormData(prev => ({ 
      ...prev, 
      invoice_type: invoiceType.toString(),
      invoice_id: '',
      invoice_number: ''
    }));
    
    // Only load invoices if an agent is selected
    if (formData.agency_id) {
      await loadInvoices(invoiceType.toString(), formData.agency_id);
    }

    if (invoiceType.toString() === 'Tests') {
      loadAllTests();
    } else {
      setAllTests([]);
    }
  };

  const [editingCommissionId, setEditingCommissionId] = useState<number | null>(null);

  const resetFormData = () => {
    setFormData({
      order_commission_number: generateOrderNumber(),
      invoice_type: 'Tests',
      commission_type: 'Invoice',
      invoice_id: '',
      invoice_number: '',
      agency_id: '',
      agency_name: '',
      agency_phone: '',
      invoice_final_bill_amount: 0,
      commission_value: 10,
      notes: '',
      disc_percentage: 0,
      roundoff: 0,
      payment_amount: 0,
      payment_mode: 'CASH',
      payment_number: generatePaymentNumber(),
      instrument_number: '',
      instrument_date: new Date().toISOString().split('T')[0],
      bank_name: '',
      branch_name: '',
      ifsc_code: '',
      transaction_reference: '',
      payment_remarks: ''
    });
    setItems([{
      item_type: 'Test',
      item_id: 0,
      item_name: '',
      item_rate: 0,
      item_qty: 1,
      commission_type: 'Percentage',
      commission_percentage: 0,
      commission_value: 0,
      gst_percentage: 0,
      gst_amount: 0,
      cess_percentage: 0,
      cess_amount: 0,
      total_amount: 0,
      invoice_total_amount: 0,
      discount_percentage: 0,
      discount_amount: 0,
      roundoff: 0,
      final_amount: 0
    }]);
    setEditingCommissionId(null);
  };

  const handleInvoiceChange = async (value: string | number | (string | number)[]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setFormData(prev => ({ 
        ...prev, 
        invoice_id: '',
        invoice_number: ''
      }));
      setItems([{
        item_type: formData.invoice_type,
        item_id: 0,
        item_name: '',
        item_rate: 0,
        item_qty: 1,
        commission_type: 'Percentage',
        commission_percentage: 0,
        commission_value: 0,
        gst_percentage: 0,
        gst_amount: 0,
        cess_percentage: 0,
        cess_amount: 0,
        total_amount: 0,
        invoice_total_amount: 0,
        discount_percentage: 0,
        discount_amount: 0,
        roundoff: 0,
        final_amount: 0
      }]);
      setEditingCommissionId(null);
      return;
    }
    const invoiceId = Array.isArray(value) ? value[0] : value;
    const invoice = invoiceOptions.find(o => o.value === invoiceId.toString());
    
    // Check if commission already exists for this invoice
    try {
      const commissionsResponse = await adminService.getOrderCommissions();
      const existingCommission = commissionsResponse.data.find(
        (c: any) => c.order_id === Number(invoiceId) && c.order_type === formData.invoice_type
      );
      
      if (existingCommission) {
        // Load existing commission data
        const commissionDetails = await adminService.getOrderCommission(existingCommission.id);
        const data = commissionDetails.data.data || commissionDetails.data;
        
        setEditingCommissionId(data.id);
        setFormData(prev => ({
          ...prev,
          order_commission_number: data.commission_number || prev.order_commission_number,
          invoice_id: invoiceId.toString(),
          invoice_number: data.order_number,
          agency_id: data.agency_id?.toString() || '',
          agency_name: data.agency_name || '',
          agency_phone: data.agency_phone || '',
          commission_type: data.commission_type || 'Invoice',
          invoice_final_bill_amount: data.invoice_final_bill_amount || 0,
          commission_value: data.commission_value || 10,
          notes: data.notes || '',
          disc_percentage: data.disc_percentage || 0,
          roundoff: data.roundoff || 0
        }));
        
        if (data.items && data.items.length > 0) {
          setItems(data.items);
        }
        
        // Load invoice details to ensure invoice_final_bill_amount is up to date
        try {
          let invoiceDetails: any = {};
          if (formData.invoice_type === 'Products') {
            const response = await salesInvoiceService.getSalesInvoice(Number(invoiceId));
            invoiceDetails = response.data.data || response.data;
          } else if (formData.invoice_type === 'Tests') {
            const response = await invoiceService.getTestInvoice(Number(invoiceId));
            invoiceDetails = response.data.data || response.data;
          } else if (formData.invoice_type === 'Clinic') {
            const response = await appointmentInvoiceService.getAppointmentInvoice(Number(invoiceId));
            invoiceDetails = response.data.data || response.data;
          }
          
          setFormData(prev => ({ 
            ...prev,
            invoice_final_bill_amount: invoiceDetails.final_amount || invoiceDetails.final_bill_amount || invoiceDetails.total_amount || invoiceDetails.grand_total || prev.invoice_final_bill_amount
          }));
        } catch (invoiceError) {
          console.error('Error loading invoice details:', invoiceError);
        }
        
        return;
      }
    } catch (error) {
      console.error('Error checking existing commission:', error);
    }
    
    // No existing commission, load invoice details
    setEditingCommissionId(null);
    try {
      let invoiceDetails: any = {};
      if (formData.invoice_type === 'Products') {
        const response = await salesInvoiceService.getSalesInvoice(Number(invoiceId));
        invoiceDetails = response.data.data || response.data;
      } else if (formData.invoice_type === 'Tests') {
        const response = await invoiceService.getTestInvoice(Number(invoiceId));
        invoiceDetails = response.data.data || response.data;
      } else if (formData.invoice_type === 'Clinic') {
        const response = await appointmentInvoiceService.getAppointmentInvoice(Number(invoiceId));
        invoiceDetails = response.data.data || response.data;
      }
      
      setFormData(prev => ({ 
        ...prev,
        order_commission_number: generateOrderNumber(),
        invoice_id: invoiceId.toString(),
        invoice_number: invoice ? invoice.label.split(' - ')[0] : '',
        invoice_final_bill_amount: invoiceDetails.final_amount || invoiceDetails.final_bill_amount || invoiceDetails.total_amount || invoiceDetails.grand_total || 0
      }));
      
      // Populate commission items from invoice items
      if (invoiceDetails.items && invoiceDetails.items.length > 0) {
        const commissionItems = invoiceDetails.items.map((item: any) => {
          const itemType = formData.invoice_type;
          const itemId = item.test_id || item.product_id || item.billing_master_id || 0;
          const itemName = item.test_name || item.product_name || item.description || '';
          const itemRate = item.rate || item.total_amount || item.amount || 0;
          
          // Find the corresponding test in allTests to get commission settings
          const testData = allTests.find(test => test.id === itemId);
          let commissionType = 'Percentage';
          let commissionPercentage = 0;
          let commissionValue = 0;
          
          if (testData && testData.commission_type === 'Percentage') {
            commissionType = 'Percentage';
            commissionPercentage = testData.commission_value || 0;
            commissionValue = parseFloat(toSafeFixed(item.total_amount * (commissionPercentage / 100), 2));
          } else if (testData && testData.commission_type === 'Flat') {
            commissionType = 'Flat';
            commissionValue = testData.commission_value || 0;
          }
          
          // GST and CESS set to 0 by default
          const gstRate = 0;
          const cessRate = 0;
          const discPercentage = 0;
          
          // Calculate amounts
          const gstAmount = 0;
          const cessAmount = 0;
          const totalAmount = parseFloat(toSafeFixed(commissionValue + gstAmount + cessAmount, 2));
          const discountAmount = parseFloat(toSafeFixed(totalAmount * (discPercentage / 100), 2));
          const finalAmount = parseFloat(toSafeFixed(totalAmount - discountAmount, 2));
          
          return {
            item_type: itemType,
            item_id: itemId,
            item_name: itemName,
            item_rate: itemRate,
            item_qty: 1,
            commission_type: commissionType,
            commission_percentage: commissionPercentage,
            commission_value: commissionValue,
            gst_percentage: gstRate,
            gst_amount: gstAmount,
            cess_percentage: cessRate,
            cess_amount: cessAmount,
            total_amount: totalAmount,
            invoice_total_amount: item.total_amount || 0,
            discount_percentage: discPercentage,
            discount_amount: discountAmount,
            roundoff: 0,
            final_amount: finalAmount
          };
        });
        setItems(commissionItems);
      } else {
        // If no items, initialize with one empty item
        setItems([{
          item_type: formData.invoice_type,
          item_id: 0,
          item_name: '',
          item_rate: 0,
          item_qty: 1,
          commission_type: 'Percentage',
          commission_percentage: 0,
          commission_value: 0,
          gst_percentage: 0,
          gst_amount: 0,
          cess_percentage: 0,
          cess_amount: 0,
          total_amount: 0,
          invoice_total_amount: 0,
          discount_percentage: 0,
          discount_amount: 0,
          roundoff: 0,
          final_amount: 0
        }]);
      }
    } catch (error) {
      setFormData(prev => ({ 
        ...prev, 
        invoice_id: invoiceId.toString(),
        invoice_number: invoice ? invoice.label.split(' - ')[0] : ''
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('percentage') || name === 'roundoff' ? parseFloat(value) || 0 : value
    }));
  };

  const handlePaymentModeChange = (mode: string) => {
    setFormData(prev => ({
      ...prev,
      payment_mode: mode,
      instrument_number: '',
      instrument_date: new Date().toISOString().split('T')[0],
      bank_name: '',
      branch_name: '',
      ifsc_code: '',
      transaction_reference: ''
    }));
  };

  const handleItemTestChange = (index: number, value: string | number | (string | number)[]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setItems(prev => prev.map((item, i) => i === index ? {
        ...item,
        item_id: 0,
        item_name: '',
        item_rate: 0,
        commission_type: 'Percentage',
        gst_percentage: 0,
        cess_percentage: 0,
        discount_percentage: 0,
        invoice_total_amount: 0
      } : item));
      return;
    }
    const testId = Array.isArray(value) ? value[0] : value;
    const selectedTest = allTests.find(item => item.id.toString() === testId.toString());
    if (selectedTest) {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        item_type: 'Test',
        item_id: selectedTest.id,
        item_name: selectedTest.name,
        item_rate: selectedTest.rate || 0,
        item_qty: 1,
        commission_type: selectedTest.commission_type || 'Percentage',
        gst_percentage: selectedTest.gst_rate || 0,
        cess_percentage: selectedTest.cess_rate || 0,
        discount_percentage: selectedTest.discount_percentage || 0
      };
      setItems(newItems);
      calculateItemTotal(index, newItems);
    }
  };

  const handleItemChange = (index: number, field: keyof CommissionItem, value: number | string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
    calculateItemTotal(index, newItems, field);
  };

  const calculateItemTotal = (index: number, itemsArray: CommissionItem[], field?: string) => {
    const item = itemsArray[index];
    
    // Calculate commission_value based on type
    let commissionValue = item.commission_value;
    
    if (item.commission_type === 'Percentage') {
      if (field === 'commission_percentage') {
        commissionValue = parseFloat(toSafeFixed(item.item_rate * (item.commission_percentage / 100), 2));
      }
      // For percentage type, commission_value is always calculated from percentage
      commissionValue = parseFloat(toSafeFixed(item.invoice_total_amount * (item.commission_percentage / 100), 2));
    } else if (item.commission_type === 'Flat') {
      // For flat type, commission_value is set directly
      commissionValue = item.commission_value;
    }
    
    // Always recalculate amounts based on current values
    const gstAmount = parseFloat(toSafeFixed(commissionValue * (item.gst_percentage / 100), 2));
    const cessAmount = parseFloat(toSafeFixed(commissionValue * (item.cess_percentage / 100), 2));
    const totalAmount = parseFloat(toSafeFixed(commissionValue + gstAmount + cessAmount, 2));
    const discountAmount = parseFloat(toSafeFixed(totalAmount * (item.discount_percentage / 100), 2));
    const finalAmount = parseFloat(toSafeFixed(totalAmount - discountAmount + item.roundoff, 2));

    itemsArray[index] = {
      ...item,
      commission_value: commissionValue,
      gst_amount: gstAmount,
      cess_amount: cessAmount,
      total_amount: totalAmount,
      discount_amount: discountAmount,
      final_amount: finalAmount
    };
    setItems([...itemsArray]);
  };

  const addItem = () => {
    setItems([...items, {
      item_type: formData.invoice_type,
      item_id: 0,
      item_name: '',
      item_rate: 0,
      item_qty: 1,
      commission_type: 'Percentage',
      commission_percentage: 0,
      commission_value: 0,
      gst_percentage: 0,
      gst_amount: 0,
      cess_percentage: 0,
      cess_amount: 0,
      total_amount: 0,
      invoice_total_amount: 0,
      discount_percentage: 0,
      discount_amount: 0,
      roundoff: 0,
      final_amount: 0
    }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    let total = 0;
    
    if (formData.commission_type === 'Percentage') {
      // Calculate commission based on final bill amount percentage
      total = formData.invoice_final_bill_amount * (formData.commission_value / 100);
    } else {
      // Calculate commission as sum of individual item commissions
      total = items.reduce((sum, item) => sum + item.final_amount, 0);
    }
    
    const discAmount = total * (formData.disc_percentage / 100);
    const final = total - discAmount + formData.roundoff;
    return { total, discAmount, final };
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (!formData.agency_id) {
      showToast('error', 'Please select an agency');
      return;
    }

    if (formData.commission_type === 'Invoice') {
      const validItems = items.filter(item => item.item_name && item.item_rate > 0);
      if (validItems.length === 0) {
        showToast('error', 'Please add at least one item');
        return;
      }
    }

    if (formData.commission_type === 'Percentage') {
      if (formData.commission_value <= 0 || formData.commission_value > 100) {
        showToast('error', 'Commission value must be between 0.1 and 100 for Percentage type');
        return;
      }
    }

    // Calculate totals to check pending amount
    const { total, discAmount, final } = calculateTotals();
    const pendingAmount = final - formData.payment_amount;
    
    if (Math.abs(pendingAmount) > 0.01) { // Allow small floating point differences
      showToast('error', `Pending amount must be 0. Current pending amount: ${pendingAmount.toFixed(2)}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const { total, discAmount, final } = calculateTotals();
      
      const validItems = items.filter(item => item.item_name && item.item_rate > 0);
      
      const commissionData = {
        order_commission_number: formData.order_commission_number,
        order_type: formData.invoice_type,
        commission_type: formData.commission_type,
        order_id: Number(formData.invoice_id),
        order_number: formData.invoice_number,
        invoice_id: Number(formData.invoice_id),
        invoice_number: formData.invoice_number,
        agency_id: Number(formData.agency_id),
        agency_name: formData.agency_name,
        agency_phone: formData.agency_phone,
        invoice_final_bill_amount: formData.invoice_final_bill_amount,
        commission_value: formData.commission_value,
        notes: formData.notes,
        total_amount: total,
        disc_percentage: formData.disc_percentage,
        disc_amount: discAmount,
        roundoff: formData.roundoff,
        final_amount: final,
        items: formData.commission_type === 'Invoice' ? validItems.map(item => ({
          item_type: item.item_type,
          item_id: item.item_id,
          item_name: item.item_name,
          item_rate: item.item_rate,
          item_qty: item.item_qty,
          commission_percentage: item.commission_percentage,
          commission_value: item.commission_value,
          gst_percentage: item.gst_percentage,
          gst_amount: item.gst_amount,
          cess_percentage: item.cess_percentage,
          cess_amount: item.cess_amount,
          total_amount: item.total_amount,
          discount_percentage: item.discount_percentage,
          discount_amount: item.discount_amount,
          roundoff: item.roundoff,
          final_amount: item.final_amount
        })) : [],
        payment: {
          payment_number: formData.payment_number,
          payment_amount: formData.payment_amount,
          payment_mode: formData.payment_mode,
          instrument_number: formData.instrument_number,
          instrument_date: formData.instrument_date,
          bank_name: formData.bank_name,
          branch_name: formData.branch_name,
          ifsc_code: formData.ifsc_code,
          transaction_reference: formData.transaction_reference,
          payment_remarks: formData.payment_remarks
        }
      };

      if (editingCommissionId) {
        await adminService.updateOrderCommission(editingCommissionId, commissionData);
        showToast('success', 'Order commission updated successfully');
      } else {
        await adminService.createOrderCommission(commissionData);
        showToast('success', 'Order commission created successfully');
      }
      setEditingCommissionId(null);
      onSave();
      resetFormData();
    } catch (error) {
      showToast('error', 'Failed to create order commission');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { total, discAmount, final } = calculateTotals();
  const pendingAmount = final - formData.payment_amount;

  useEffect(() => {
    // Update payment_amount to match final total
    setFormData(prev => ({ ...prev, payment_amount: final }));
  }, [final]);

  const testOptions = allTests.map(item => ({
    value: item.id.toString(),
    label: item.name
  }));

  return (
    <div className="bg-white rounded-lg shadow mb-4 mt-1">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Create Invoice Commission</h2>
        <button type="button" onClick={onToggleCollapse} className="text-gray-500 hover:text-gray-700">
          {isCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
        </button>
      </div>

      {!isCollapsed && (
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-6">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Commission #</label>
              <input
                type="text"
                value={formData.order_commission_number}
                readOnly
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Type *</label>
              <SearchableDropdown
                key={`invoice-type-${formData.invoice_type}`}
                options={[
                  // { value: 'Sales', label: 'Sales' },
                  { value: 'Tests', label: 'Test' },
                  { value: 'Clinic', label: 'Clinic' }
                ]}
                value={formData.invoice_type}
                onChange={handleInvoiceTypeChange}
                placeholder="Select invoice type"
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Commission Type *</label>
              <SearchableDropdown
                key={`commission-type-${formData.commission_type}`}
                options={[
                  { value: 'Percentage', label: 'Percentage' },
                  { value: 'Invoice', label: 'Invoice' }
                ]}
                value={formData.commission_type}
                onChange={(value: string | number | (string | number)[]) => {
                  const commissionType = Array.isArray(value) ? value[0] : value;
                  setFormData(prev => ({
                    ...prev,
                    commission_type: commissionType.toString(),
                    commission_value: commissionType.toString() === 'Percentage' ? (prev.commission_value || 10) : 0
                  }));
                }}
                placeholder="Select commission type"
                multiple={false}
                searchable={true}
              />
            </div>

            {formData.commission_type === 'Percentage' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Commission Value * (0-100%)</label>
                <NumericInput
                  value={formData.commission_value}
                  onChange={(value) => setFormData(prev => ({ ...prev, commission_value: value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                  step={0.1}
                  min={0.1}
                  max={100}
                  decimalPlaces={2}
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Agency *</label>
              <SearchableDropdown
                key={`agency-${agencyOptions.length}`}
                options={agencyOptions && agencyOptions.length > 0 ? agencyOptions : []}
                value={formData.agency_id}
                onChange={handleAgencyChange}
                placeholder={agencyOptions.length === 0 ? "No agencies available" : "Select agency"}
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice *</label>
              <SearchableDropdown
                key={`invoice-${formData.invoice_type}-${invoiceOptions.length}`}
                options={invoiceOptions && invoiceOptions.length > 0 ? invoiceOptions : []}
                value={formData.invoice_id}
                onChange={handleInvoiceChange}
                placeholder={invoiceOptions.length === 0 ? "No invoices available" : "Select invoice"}
                multiple={false}
                searchable={true}
                onSearch={async () => invoiceOptions}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Final Bill Amount</label>
              <NumericInput
                value={formData.invoice_final_bill_amount}
                onChange={(value) => setFormData(prev => ({ ...prev, invoice_final_bill_amount: value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100"
                step={0.01}
                min={0}
                disabled
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={1}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary resize-none overflow-y-auto"
                style={{ minHeight: '30px', maxHeight: '30px' }}
                placeholder="Enter notes..."
              />
            </div>
          </div>



          {formData.commission_type === 'Invoice' && (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-md font-semibold">Commission Items</h3>
                <button type="button" onClick={addItem} className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded">
                  <Plus className="h-3 w-3 mr-1" />
                  Add Item
                </button>
              </div>

              <div className="mb-6 overflow-x-auto">
                <table className="border border-gray-200 w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-left">Item Name</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Rate</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Total Amount</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Commission Type</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Commission %</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Commission Value</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">GST%</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">CESS%</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Disc%</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Final</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-2 py-2">
                          <SearchableDropdown
                            key={`test-${index}-${item.item_id}`}
                            options={testOptions}
                            value={item.item_id.toString()}
                            onChange={(value) => handleItemTestChange(index, value)}
                            placeholder="Select test"
                            multiple={false}
                            searchable={true}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            value={item.item_rate}
                            readOnly
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center bg-gray-100"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            value={item.invoice_total_amount}
                            readOnly
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center bg-gray-100"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <SearchableDropdown
                            key={`commission-type-${index}-${item.commission_type}`}
                            options={[
                              { value: 'Percentage', label: 'Percentage' },
                              { value: 'Flat', label: 'Flat' }
                            ]}
                            value={item.commission_type}
                            onChange={(value) => handleItemChange(index, 'commission_type', Array.isArray(value) ? value[0] : value)}
                            placeholder="Type"
                            multiple={false}
                            searchable={false}
                          />
                        </td>
                        <td className="px-2 py-2">
                          {item.commission_type === 'Percentage' ? (
                            <input
                              type="number"
                              value={item.commission_percentage}
                              onChange={(e) => handleItemChange(index, 'commission_percentage', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                              step="0.1"
                            />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            value={item.commission_value}
                            onChange={(e) => handleItemChange(index, 'commission_value', parseFloat(e.target.value) || 0)}
                            readOnly={item.commission_type === 'Percentage'}
                            className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center ${item.commission_type === 'Percentage' ? 'bg-gray-100' : ''}`}
                            step="0.01"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <NumericInput
                            value={item.gst_percentage}
                            onChange={(value) => handleItemChange(index, 'gst_percentage', value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                            step={0.1}
                            decimalPlaces={2}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <NumericInput
                            value={item.cess_percentage}
                            onChange={(value) => handleItemChange(index, 'cess_percentage', value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                            step={0.1}
                            decimalPlaces={2}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <NumericInput
                            value={item.discount_percentage}
                            onChange={(value) => handleItemChange(index, 'discount_percentage', value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                            step={0.1}
                            decimalPlaces={2}
                          />
                        </td>
                        <td className="px-2 py-2 text-sm text-center">{toSafeFixed(item.final_amount, 2)}</td>
                        <td className="px-2 py-2 text-center">
                          <button type="button" onClick={() => removeItem(index)} className="text-red-600 hover:text-red-800" disabled={items.length === 1}>
                            <Minus className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Payment Information Section */}
          <div className="mb-6">
            <h3 className="text-md font-semibold mb-4">Payment Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Number</label>
                <input
                  type="text"
                  value={formData.payment_number}
                  readOnly
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount *</label>
                <NumericInput
                  name="payment_amount"
                  value={final}
                  onChange={(value) => setFormData(prev => ({ ...prev, payment_amount: value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                  step={0.01}
                  min={0}
                  allowNegative={false}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Pending Amount</label>
                <input
                  type="number"
                  value={toSafeFixed(pendingAmount, 2)}
                  readOnly
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode *</label>
                <SearchableDropdown
                  options={paymentModes.map(mode => ({ value: mode, label: mode }))}
                  value={formData.payment_mode}
                  onChange={(v) => handlePaymentModeChange(v.toString())}
                  placeholder="Select mode"
                  multiple={false}
                  searchable={true}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Remarks</label>
                <input
                  type="text"
                  name="payment_remarks"
                  value={formData.payment_remarks}
                  onChange={handleInputChange}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                />
              </div>
            </div>

            {/* Payment Mode Specific Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              {(() => {
                const fields = [];

                // Instrument Number - shown for CHEQUE, CARD, BANK_TRANSFER
                if (['CHEQUE', 'CARD', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="instrument_number">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Instrument Number *</label>
                      <input
                        type="text"
                        name="instrument_number"
                        value={formData.instrument_number}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                  );
                }

                // Instrument Date - shown for CHEQUE, CARD, UPI, BANK_TRANSFER
                if (['CHEQUE', 'CARD', 'UPI', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="instrument_date">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Instrument Date *</label>
                      <DatePicker
                        value={formData.instrument_date}
                        onChange={(v) => setFormData(prev => ({ ...prev, instrument_date: v }))}
                        required
                      />
                    </div>
                  );
                }

                // Transaction Reference - shown for UPI
                if (formData.payment_mode === 'UPI') {
                  fields.push(
                    <div key="transaction_reference">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Transaction Reference</label>
                      <input
                        type="text"
                        name="transaction_reference"
                        value={formData.transaction_reference}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  );
                }

                // Bank Name - shown for CHEQUE, BANK_TRANSFER
                if (['CHEQUE', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="bank_name">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name *</label>
                      <input
                        type="text"
                        name="bank_name"
                        value={formData.bank_name}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                  );
                }

                // Branch Name - shown for CHEQUE, BANK_TRANSFER
                if (['CHEQUE', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="branch_name">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Branch Name</label>
                      <input
                        type="text"
                        name="branch_name"
                        value={formData.branch_name}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  );
                }

                // IFSC Code - shown for CHEQUE, BANK_TRANSFER
                if (['CHEQUE', 'BANK_TRANSFER'].includes(formData.payment_mode)) {
                  fields.push(
                    <div key="ifsc_code">
                      <label className="block text-xs font-medium text-gray-700 mb-1">IFSC Code</label>
                      <input
                        type="text"
                        name="ifsc_code"
                        value={formData.ifsc_code}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  );
                }

                return fields;
              })()}
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-end">
            <div className="w-full md:w-1/2 bg-gray-50 p-4 rounded">
              <h4 className="font-semibold mb-3">Commission Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total:</span>
                  <span>{toSafeFixed(total, 2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Discount %:</span>
                  <NumericInput
                    value={formData.disc_percentage}
                    onChange={(value) => setFormData(prev => ({ ...prev, disc_percentage: value }))}
                    className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded"
                    step={0.1}
                    decimalPlaces={2}
                  />
                </div>
                <div className="flex justify-between">
                  <span>Discount Amount:</span>
                  <span>{toSafeFixed(discAmount, 2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg border-t pt-2">
                  <span>Final Total:</span>
                  <span>{toSafeFixed(final, 2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-2 mt-6">
            <button
              type="button"
              onClick={resetFormData}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded"
            >
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-secondary rounded disabled:opacity-50 disabled:cursor-not-allowed">
              {isSubmitting ? 'Saving...' : (editingCommissionId ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default OrderCommissionForm;
